import React, { useEffect, useState } from 'react';
import { billingService, PricingConfiguration, HostBalance } from '../../services/billingService';
import { useAppSelector } from '../../store/hooks';
import { Wallet, AlertTriangle, CheckCircle, DollarSign, TrendingDown } from 'lucide-react';
import clsx from 'clsx';
import LoadingSpinner from '../UI/LoadingSpinner';
import { logError } from '../../utils/errorLogger';

export interface CostBreakdown {
  label: string;
  amount: number;
  quantity?: number;
  unitPrice?: number;
}

export interface BillingActionPreviewProps {
  /** Cost breakdown items */
  costs: CostBreakdown[];
  /** Optional: Show current balance */
  showBalance?: boolean;
  /** Optional: Show balance after action */
  showBalanceAfter?: boolean;
  /** Optional: Custom balance (if not provided, fetches from API) */
  currentBalance?: HostBalance | null;
  /** Optional: Custom pricing (if not provided, fetches from API) */
  pricing?: PricingConfiguration | null;
  /** Optional: Show warning if balance will go negative */
  showWarning?: boolean;
  /** Optional: Compact mode for smaller displays */
  compact?: boolean;
  /** Optional: Title for the preview */
  title?: string;
  /** Optional: Additional info text */
  infoText?: string;
}

const BillingActionPreview: React.FC<BillingActionPreviewProps> = ({
  costs,
  showBalance = true,
  showBalanceAfter = true,
  currentBalance: propBalance,
  pricing: propPricing,
  showWarning = true,
  compact = false,
  title,
  infoText
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [balance, setBalance] = useState<HostBalance | null>(propBalance || null);
  const [pricing, setPricing] = useState<PricingConfiguration | null>(propPricing || null);
  const [loading, setLoading] = useState(!propBalance || !propPricing);

  // Calculate total cost
  const totalCost = costs.reduce((sum, item) => sum + item.amount, 0);

  // Fetch balance and pricing if not provided
  useEffect(() => {
    if (propBalance && propPricing) {
      setBalance(propBalance);
      setPricing(propPricing);
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const [balanceData, pricingData] = await Promise.all([
          showBalance ? billingService.getMyBalance().catch(() => null) : Promise.resolve(null),
          billingService.getCurrentPricing().catch(() => null)
        ]);
        setBalance(balanceData);
        setPricing(pricingData);
      } catch (error) {
        logError('Failed to fetch billing data', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [propBalance, propPricing, showBalance]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-4">
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  const balanceAmount = balance ? parseFloat(balance.balance) : 0;
  const balanceAfter = balanceAmount - totalCost;
  const willGoNegative = balanceAfter < 0;
  const isLowBalance = balanceAmount < totalCost && balanceAmount >= 0;

  if (compact) {
    return (
      <div className={clsx(
        'rounded-lg border p-3',
        theme === 'dark'
          ? 'bg-gray-900/50 border-gray-700'
          : 'bg-gray-50 border-gray-200'
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <DollarSign className={clsx(
              'h-4 w-4',
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            )} />
            <span className={clsx(
              'text-sm font-medium',
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            )}>
              Total Cost:
            </span>
          </div>
          <span className={clsx(
            'text-sm font-semibold',
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          )}>
            {totalCost.toFixed(2)} JOD
          </span>
        </div>
        {showBalance && balance && (
          <div className="mt-2 flex items-center justify-between text-xs">
            <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
              Balance After:
            </span>
            <span className={clsx(
              'font-medium',
              willGoNegative
                ? 'text-red-600 dark:text-red-400'
                : theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            )}>
              {balanceAfter.toFixed(2)} JOD
            </span>
          </div>
        )}
        {showWarning && willGoNegative && (
          <div className="mt-2 flex items-center space-x-1 text-xs text-red-600 dark:text-red-400">
            <AlertTriangle className="h-3 w-3" />
            <span>Balance will go negative</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={clsx(
      'rounded-lg border-2 p-4 space-y-3',
      willGoNegative && showWarning
        ? 'border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10'
        : isLowBalance && showWarning
        ? 'border-yellow-300 dark:border-yellow-800 bg-yellow-50/50 dark:bg-yellow-900/10'
        : theme === 'dark'
        ? 'bg-gray-900/50 border-gray-700'
        : 'bg-gray-50 border-gray-200'
    )}>
      {title && (
        <div className="flex items-center space-x-2">
          <Wallet className={clsx(
            'h-5 w-5',
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          )} />
          <h3 className={clsx(
            'text-sm font-semibold',
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          )}>
            {title}
          </h3>
        </div>
      )}

      {/* Current Balance */}
      {showBalance && balance && (
        <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700">
          <span className={clsx(
            'text-sm',
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          )}>
            Current Balance:
          </span>
          <span className={clsx(
            'text-sm font-semibold',
            balanceAmount < 0
              ? 'text-red-600 dark:text-red-400'
              : theme === 'dark' ? 'text-white' : 'text-gray-900'
          )}>
            {balanceAmount.toFixed(2)} JOD
          </span>
        </div>
      )}

      {/* Cost Breakdown */}
      {costs.length > 0 && (
        <div className="space-y-2">
          <div className={clsx(
            'text-xs font-medium uppercase tracking-wider mb-2',
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          )}>
            Cost Breakdown
          </div>
          {costs.map((cost, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex-1">
                <span className={clsx(
                  'text-sm',
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                )}>
                  {cost.label}
                </span>
                {cost.quantity !== undefined && cost.unitPrice !== undefined && (
                  <span className={clsx(
                    'text-xs ml-2',
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                  )}>
                    ({cost.quantity} × {cost.unitPrice.toFixed(2)} JOD)
                  </span>
                )}
              </div>
              <span className={clsx(
                'text-sm font-medium',
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                {cost.amount.toFixed(2)} JOD
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Total Cost */}
      <div className="flex items-center justify-between pt-2 border-t-2 border-gray-300 dark:border-gray-600">
        <span className={clsx(
          'text-base font-semibold',
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        )}>
          Total Cost:
        </span>
        <span className={clsx(
          'text-lg font-bold',
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        )}>
          {totalCost.toFixed(2)} JOD
        </span>
      </div>

      {/* Balance After */}
      {showBalanceAfter && balance && (
        <div className={clsx(
          'flex items-center justify-between py-2 rounded-md px-3',
          willGoNegative
            ? 'bg-red-100 dark:bg-red-900/20'
            : theme === 'dark'
            ? 'bg-gray-800/50'
            : 'bg-white'
        )}>
          <div className="flex items-center space-x-2">
            <TrendingDown className={clsx(
              'h-4 w-4',
              willGoNegative
                ? 'text-red-600 dark:text-red-400'
                : theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            )} />
            <span className={clsx(
              'text-sm font-medium',
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            )}>
              Balance After:
            </span>
          </div>
          <span className={clsx(
            'text-sm font-bold',
            willGoNegative
              ? 'text-red-600 dark:text-red-400'
              : theme === 'dark' ? 'text-white' : 'text-gray-900'
          )}>
            {balanceAfter.toFixed(2)} JOD
          </span>
        </div>
      )}

      {/* Warnings */}
      {showWarning && (
        <>
          {willGoNegative && (
            <div className="flex items-start space-x-2 p-3 rounded-md bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-300">
                  Insufficient Balance
                </p>
                <p className="text-xs text-red-700 dark:text-red-400 mt-1">
                  Your balance will go negative after this action.
                </p>
              </div>
            </div>
          )}
          {isLowBalance && !willGoNegative && (
            <div className="flex items-start space-x-2 p-3 rounded-md bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-800">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                  Low Balance
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                  Your balance is low. Consider topping up to avoid interruptions.
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Info Text */}
      {infoText && (
        <p className={clsx(
          'text-xs',
          theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
        )}>
          {infoText}
        </p>
      )}
    </div>
  );
};

export default BillingActionPreview;

